import { useEffect, useState } from "react";

/**
 * Ticking countdown for the Offer Popup.
 * @param {string} expiresAt - ISO datetime string (may be empty)
 * @param {"inline"|"blocks"} style - display variant
 * @param {() => void} onExpire - called once when the timer hits zero
 */
export default function OfferCountdown({ expiresAt, style = "inline", onExpire }) {
  const target = expiresAt ? new Date(expiresAt).getTime() : NaN;
  const isValid = !Number.isNaN(target);

  const [now, setNow] = useState(() => Date.now());
  const [fired, setFired] = useState(false);

  useEffect(() => {
    if (!isValid) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isValid, target]);

  useEffect(() => {
    if (!isValid) return;
    if (target - now <= 0 && !fired) {
      setFired(true);
      if (onExpire) onExpire();
    }
  }, [now, target, isValid, fired, onExpire]);

  if (!isValid) return null;

  const diff = Math.max(0, target - now);
  if (diff <= 0) return null;

  const totalSecs = Math.floor(diff / 1000);
  const days = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  const pad = (n) => String(n).padStart(2, "0");

  if (style === "blocks") {
    const Block = ({ v, l, tid }) => (
      <div className="flex flex-col items-center" data-testid={tid}>
        <div className="bg-zinc-900 text-white font-display font-black text-2xl md:text-3xl tabular-nums rounded-lg px-3 py-2 min-w-[54px] text-center leading-none">
          {pad(v)}
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-widest font-bold text-zinc-500">{l}</div>
      </div>
    );
    return (
      <div className="mt-4 flex items-center justify-center gap-2" data-testid="offer-countdown-blocks">
        <Block v={days} l="Days" tid="cd-days" />
        <div className="text-zinc-300 font-black text-2xl pb-4">:</div>
        <Block v={hours} l="Hours" tid="cd-hours" />
        <div className="text-zinc-300 font-black text-2xl pb-4">:</div>
        <Block v={mins} l="Mins" tid="cd-mins" />
        <div className="text-zinc-300 font-black text-2xl pb-4">:</div>
        <Block v={secs} l="Secs" tid="cd-secs" />
      </div>
    );
  }

  // inline pill
  return (
    <div
      className="mt-4 inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 rounded-full text-sm font-bold tabular-nums shadow"
      data-testid="offer-countdown-inline"
    >
      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
      Ends in {pad(days)}d {pad(hours)}h {pad(mins)}m {pad(secs)}s
    </div>
  );
}
